import { REGEX, replaceIndex } from '../../versa/table.js';
import db from '../../versa/db.js';
import { getSialApiHost } from '../api.js';

const MAX_WH_ID = 2147483647;

function isWarehouseActive(r)
{
	return !(r.is_disabled === true || r.is_disabled === 1 || r.is_disabled === '1');
}

/**
 * @param {unknown[]} rows
 * @param {string|number} cid
 */
function filterPhysical(rows, cid)
{
	return (rows || [])
		.filter((r) =>
			String(r.company_id) === String(cid)
			&& isWarehouseActive(r)
			&& Number(r.warehouse_type ?? 1) === 2)
		.sort((a, b) => Number(a.id) - Number(b.id));
}

/**
 * Lista armazéns da empresa: GET direto (sem msgbox) para refletir API; IDB só se falhar.
 */
async function loadWarehousesForCompany(companyId)
{
	const host = getSialApiHost();
	const url = `${host}/warehouses?limit=0`;
	try
	{
		const r = await fetch(url, { credentials: 'include' });
		if (!r.ok)
			throw new Error(String(r.status));
		const all = await r.json();
		return (Array.isArray(all) ? all : []).filter((w) => String(w.company_id) === String(companyId));
	}
	catch (_)
	{
		const range = IDBKeyRange.bound([companyId, 0], [companyId, MAX_WH_ID]);
		return (await db.list('warehouses', range, 1)) || [];
	}
}

/**
 * UUID do campo empresa + evento publish + valor exibido / datalist (nick).
 */
function resolveCompanyId(fieldCtx, key)
{
	let _watchKey = fieldCtx._key;
	if (fieldCtx.getId().match(REGEX) && _watchKey && _watchKey.match(REGEX))
		_watchKey = replaceIndex(_watchKey, fieldCtx.getId().match(REGEX).pop());

	let companyId = _watchKey ? fieldCtx.locate(_watchKey)?.getValue() : null;
	if (companyId === undefined)
		companyId = null;
	if (companyId != null && companyId !== '' && typeof companyId === 'string' && /^\d+$/.test(companyId))
		companyId = Number(companyId);

	if ((companyId == null || companyId === '') && key != null && key !== '' && !Array.isArray(key))
		companyId = typeof key === 'string' && /^\d+$/.test(key) ? Number(key) : key;

	const inp = document.querySelector('#create-outputs #company_id');
	const raw = inp ? String(inp.value || '').trim() : '';
	if (raw)
	{
		if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw))
			companyId = raw;
		else
		{
			const lid = inp.getAttribute('list');
			const dl = lid ? document.getElementById(lid) : null;
			if (dl)
			{
				for (const o of dl.options)
				{
					if (String(o.value) === raw)
					{
						companyId = o.value;
						break;
					}
					if ((o.textContent || '').trim() === raw)
					{
						companyId = o.value;
						break;
					}
				}
			}
		}
	}

	return companyId;
}

/**
 * Gerar saídas: &lt;select&gt; apenas com armazéns físicos ativos (warehouse_type=2, não inativo).
 * Obrigatório só quando há mais de um físico. Com um único físico, o backend aplica automaticamente se vier vazio.
 */
function fillPhysicalWarehouseSelect(selector, physicalRows)
{
	const sel = document.querySelector(selector);
	if (!sel || sel.tagName !== 'SELECT')
		return;
	const prev = sel.value;
	const n = (physicalRows || []).length;
	const multi = n > 1;

	sel.innerHTML = '';
	const opt0 = document.createElement('option');
	opt0.value = '';

	if (n === 0)
	{
		opt0.textContent = '— Sem armazém físico ativo: picking em todos os armazéns —';
		sel.appendChild(opt0);
		sel.required = false;
		sel.disabled = true;
	}
	else if (n === 1)
	{
		const r = physicalRows[0];
		opt0.textContent = '— Único físico ativo (deixe assim ou escolha abaixo) —';
		sel.appendChild(opt0);
		const o = document.createElement('option');
		o.value = String(r.id);
		o.textContent = r.name || `Armazém ${r.id}`;
		sel.appendChild(o);
		sel.required = false;
		sel.disabled = false;
		sel.value = String(r.id);
	}
	else
	{
		opt0.textContent = '— Selecione um armazém físico —';
		sel.appendChild(opt0);
		for (const r of physicalRows)
		{
			const o = document.createElement('option');
			o.value = String(r.id);
			o.textContent = r.name || `Armazém ${r.id}`;
			sel.appendChild(o);
		}
		sel.required = true;
		sel.disabled = false;
		if (prev && [...sel.options].some((o) => o.value === prev))
			sel.value = prev;
		else
			sel.value = '';
	}

	const titleEl = document.querySelector('#create-outputs #co-wh-title');
	const hintEl = document.querySelector('#create-outputs #co-wh-hint');
	if (titleEl)
	{
		if (n === 0)
			titleEl.textContent = 'Armazém físico';
		else if (multi)
			titleEl.textContent = 'Armazém físico (obrigatório — há mais de um ativo)';
		else
			titleEl.textContent = 'Armazém físico';
	}
	if (hintEl)
	{
		if (n === 0)
		{
			hintEl.innerHTML = 'Esta empresa não tem <strong>armazém físico ativo</strong> cadastrado. O picking usa saldo em <strong>todos</strong> os armazéns (FEFO). Com armazém no cabeçalho (quando aplicável), a saída usa esse id; sem filtro, o da primeira linha alocada.<br><strong>Faturamento/NF-e:</strong> uma NF por pedido, salvo cancelamento/inutilização.';
		}
		else if (multi)
		{
			hintEl.innerHTML = 'Há <strong>mais de um armazém físico ativo</strong>. É <strong>obrigatório</strong> escolher <strong>exatamente um</strong> na lista. Locais físicos distintos não misturam entradas, saídas e separações.';
		}
		else
		{
			hintEl.innerHTML = 'Só existe <strong>um armazém físico ativo</strong> — ele já vem selecionado; você pode confirmar ou alterar. Se deixar a opção “único”, o sistema usa esse armazém no cabeçalho da saída.';
		}
	}
}

export default function ()
{
	const SHARED = { _this: null };

	return {
		_store: 'warehouses',
		_key: 'company_id',
		_index: 1,

		async actions(ev, publisher, key)
		{
			const action = async (companyId) =>
			{
				const rows = await loadWarehousesForCompany(companyId);
				const physical = filterPhysical(rows, companyId);
				fillPhysicalWarehouseSelect(this.selector, physical);
				const selNode = document.querySelector(this.selector);
				if (selNode && typeof this.setValue === 'function')
					this.setValue(selNode.value || '');
				this.refresh();
			};

			const companyId = resolveCompanyId(this, key);
			if (companyId != null && companyId !== ''
				&& (typeof companyId === 'string' || (typeof companyId === 'number' && !isNaN(companyId) && companyId >= 0)))
				this.preventMultiCalls(SHARED, action, () => this.refresh(), [companyId]);
			else
			{
				const sel = document.querySelector(this.selector);
				if (sel && sel.tagName === 'SELECT')
				{
					sel.innerHTML = '';
					const o = document.createElement('option');
					o.value = '';
					o.textContent = '— Escolha a empresa primeiro —';
					sel.appendChild(o);
					sel.required = false;
					sel.disabled = true;
					if (typeof this.setValue === 'function')
						this.setValue('');
					this.refresh();
				}
			}
		},
	};
}
